import { BodyRegular, FlexColLg, FlexColRg, FlexEnd, FlexSpaceBetween } from 'components/Elements';
import React, { ChangeEvent } from 'react';
import { theme } from 'theme';
import strings from '../../../strings/strings.json';
import useKeyboardTrap from 'hooks/useKeyboardTrap';
import Notification, { NotificationType } from 'components/notifiers/Notification';
import { FileUploadButton, FileUploadContainer, HiddenFileInput } from 'components/fileUpload/FileUpload';
import { FileType, STRATEGIES } from 'components/fileUpload/strategies/strategies';
import UploadIcon from 'assets/icons/controls/UploadIcon';
import { IconSize } from 'assets/icons/icon-sizes';
import Button from 'components/buttons/Button';
import Modal from 'components/modals/Modal';
import TextAreaField from 'components/forms/TextAreaField';
import { styled } from 'styled-components';

type SelectImage = {
    file: File | null;
    description: string;
};

type SelectImageModalProps = {
    setImage: (imageFile: File, imageDescription: string) => void;
    closeModal: () => void;
};

export default function SelectImageModal({ setImage, closeModal }: SelectImageModalProps): JSX.Element {
    const [imageFile, setImageFile] = React.useState<SelectImage>({ file: null, description: '' });
    const [error, setError] = React.useState<string>('');
    const [interactedWithDescription, setInteractedWithDescription] = React.useState<boolean>(false);

    const { ref } = useKeyboardTrap(closeModal);
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const imageDescriptionRef = React.useRef<HTMLTextAreaElement>(null);

    const imageStrategy = STRATEGIES.find((x) => x.fileType === FileType.IMAGE)!;

    function confirmFile() {
        setInteractedWithDescription(true);

        if (!imageFile.file) {
            setError('Select an image.');
            return;
        }
        if (!imageFile.description) {
            imageDescriptionRef.current?.focus();
            return;
        }
        setImage(imageFile.file!, imageFile.description);
        closeModal();
    }

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        setError('');
        const file = !e.target.files ? null : e.target.files[0];
        if (!file) {
            return;
        }
        if (!imageStrategy.mimeTypes.includes(file.type)) {
            setError(imageStrategy.invalidTypeText);
            setImageFile((prev) => ({ ...prev, file: null }));
            return;
        }
        if (file.size > imageStrategy.maxFileSize) {
            setError(imageStrategy.invalidSizeText);
            setImageFile((prev) => ({ ...prev, file: null }));
            return;
        }

        setImageFile((prev) => ({
            ...prev,
            file: e.target.files![0],
        }));
    };

    const handleButtonKeyDown = (event: React.KeyboardEvent<HTMLLabelElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
            if (!fileInputRef.current) return;
            fileInputRef.current.click();
        }
    };

    return (
        <Modal
            headlineText={strings.selectFileForm.titleImage}
            onClickCloseButton={closeModal}
            modalBody={
                <FlexColLg ref={ref} style={{ width: '100%' }}>
                    <FileUploadContainer>
                        <FlexSpaceBetween style={{ width: '100%' }}>
                            <BodyRegular>
                                {imageFile.file ? imageFile.file.name : imageStrategy.validationText}
                            </BodyRegular>
                            <FileUploadButton
                                htmlFor="upload-input"
                                tabIndex={0}
                                aria-label={strings.fileUpload.selectFileButtonAria}
                                onKeyDown={handleButtonKeyDown}
                            >
                                {strings.fileUpload.selectFileButtonTitle}
                            </FileUploadButton>
                            <HiddenFileInput
                                id="upload-input"
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                            />
                        </FlexSpaceBetween>
                        {imageFile.file && <Image src={URL.createObjectURL(imageFile.file)} alt={''} />}
                        {error && (
                            <Notification notificationType={NotificationType.DANGER} title={error} maxWidth="100%" />
                        )}
                    </FileUploadContainer>
                    <FlexColRg>
                        <Notification
                            notificationType={NotificationType.INFO}
                            title={strings.fileUpload.imageDescriptionExplanationTitle}
                            maxWidth={'100%'}
                        />
                        <TextAreaField
                            height={200}
                            ref={imageDescriptionRef}
                            fieldName={strings.fileUpload.imageDescriptionLabel}
                            aria={strings.fileUpload.imageDescriptionAria}
                            maxLength={2000}
                            charactersLeft={2000 - imageFile.description.length}
                            required
                            errorMessage={
                                interactedWithDescription && !imageFile.description
                                    ? strings.fileUpload.imageAltTextRequiredError
                                    : undefined
                            }
                            onChange={(event: { target: { value: string } }) =>
                                setImageFile((prev) => {
                                    return {
                                        ...prev,
                                        description: event.target.value,
                                    };
                                })
                            }
                            onFocus={() => setInteractedWithDescription(true)}
                        />
                    </FlexColRg>
                    <FlexEnd>
                        <Button
                            title={strings.selectFileForm.uploadFileButtonTitle}
                            aria={strings.selectFileForm.uploadFileButtonAria}
                            colour="PRIMARY"
                            onClick={confirmFile}
                            icon={<UploadIcon colour={theme.cardBackground} size={IconSize.SMALL} />}
                        />
                    </FlexEnd>
                </FlexColLg>
            }
        />
    );
}

const Image = styled.img`
    object-fit: contain;
    width: 100%;
    max-height: 350px;
`;
